// stress.ts
// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
//
// COP — a DEV-ONLY synthetic STRESS theater: N lattice-spanning dots for in-browser
// render/scale measurement (the browser-side companion to scripts/pipeline-ivm-bench.ts).
// Reuses the generic theater loader so the picture flows the SAME bus → LOCAL PDP (I4) →
// projection → paint → render path as a real theater — it exercises the WHOLE data→screen
// pipeline at scale, not a stubbed subset.
//
// Reached ONLY via the explicit design sandbox: ?mock=1&scenario=stress (+ optional
// &stressN=<count>, default 100000, capped 250k). Registered ONLY in a dev build
// (import.meta.env.DEV) so it never appears in a shipped scenario list.
//
// Determinism: markings/affiliations/positions are a pure function of the index (no
// Math.random), so two runs render the identical picture — reproducible measurement.

import type { EnvelopeBus } from "../host/envelopeBus.ts";
import {
  startTheaterScenario,
  type TheaterDef,
  type TheaterHandle,
  type TheaterRecord,
} from "./theaters.ts";
import type { Affiliation } from "../../../../packages/schema/ts/envelope.ts";

const AFFIL: Affiliation[] = ["friend", "hostile", "neutral", "unknown", "pending"];

/** Lattice-spanning markings (mirrors scripts/pipeline-ivm-bench.ts): a handful of distinct
 *  CLS_ID classes over N objects — the shape the engine interns K classes for. */
const MARKINGS: Array<{
  classification: string;
  releasability: string[];
  mission?: string[];
  caveats?: string[];
}> = [
  { classification: "UNCLASSIFIED", releasability: ["USA"] },
  { classification: "CONFIDENTIAL", releasability: ["USA"] },
  { classification: "SECRET", releasability: ["USA"] },
  { classification: "SECRET", releasability: ["GBR"] },
  { classification: "SECRET", releasability: ["USA"], caveats: ["NOFORN"] },
  { classification: "TOP_SECRET", releasability: ["USA", "GBR"] },
  { classification: "TOP_SECRET", releasability: ["USA"], mission: ["sci:SI"] },
  { classification: "TOP_SECRET", releasability: ["USA"], mission: ["sci:HCS"] },
];

/** N from ?stressN= (default 100000, capped at 250k so a fat-fingered value can't wedge the tab). */
function stressCount(): number {
  try {
    const v = Number(new URLSearchParams(window.location.search).get("stressN"));
    if (Number.isFinite(v) && v > 0) return Math.min(Math.floor(v), 250_000);
  } catch {
    /* no window — fall through */
  }
  return 100_000;
}

// Cluster around the COP's default camera (cop.ts INITIAL_VIEW: lon 56.6, lat 26.6, zoom 8) so the
// stress picture is IMMEDIATELY visible (a dense field, not a dark map) and stresses GPU overdraw.
// ±6°lon × ±4°lat: thousands are in the default zoom-8 viewport; zoom out one notch to see all N.
const CENTER_LON = 56.6;
const CENTER_LAT = 26.6;
const SPREAD_LON = 6;
const SPREAD_LAT = 4;

/** N deterministic lattice-spanning records densely clustered on the default view; ~1/5 move
 *  (glide test), ~1/50 are alert-kind (decoration/verdict exercise). */
export function buildStressRecords(n: number): TheaterRecord[] {
  const recs: TheaterRecord[] = [];
  const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
  for (let i = 0; i < n; i++) {
    const m = MARKINGS[i % MARKINGS.length];
    const aff = AFFIL[i % AFFIL.length];
    const row = Math.floor(i / cols);
    const col = i % cols;
    const lon = CENTER_LON - SPREAD_LON + (col / cols) * SPREAD_LON * 2;
    const lat = CENTER_LAT - SPREAD_LAT + (row / cols) * SPREAD_LAT * 2;
    const moving = i % 5 === 0;
    recs.push({
      id: `stress-${i}`,
      kind: i % 50 === 0 ? "alert" : "track",
      domain: i % 2 === 0 ? "surface" : "air",
      affiliation: aff,
      type: "stress",
      name: `STRESS-${i}`,
      lat,
      lon,
      headingDeg: (i * 37) % 360,
      speedKn: moving ? 180 + (i % 240) : 0, // modest speeds so the escape-velocity guard never trips
      classification: m.classification,
      releasability: m.releasability,
      ...(m.mission ? { mission: m.mission } : {}),
      ...(m.caveats ? { caveats: m.caveats } : {}),
    });
  }
  return recs;
}

/** Start the stress picture: emit all N once, then re-emit per tick (position-only firehose
 *  for the movers). tickMs defaults higher than a real theater so a huge working set isn't
 *  re-published aggressively during a measurement. */
export function startStressScenario(bus: EnvelopeBus, tickMs = 1_000): TheaterHandle {
  const n = stressCount();
  const def: TheaterDef = {
    id: "stress",
    label: `Stress ${n.toLocaleString()}`,
    records: buildStressRecords(n),
  };
  // eslint-disable-next-line no-console
  console.log(`[stress] publishing ${n.toLocaleString()} synthetic dots (?stressN to change)`);
  return startTheaterScenario(def, bus, Math.max(tickMs, 2_000));
}
